import { Stack, Text } from '@ecoinvent/ui';

import { ActivityType } from 'hooks/data/search/types';

export const activityTypes: Record<ActivityType, string> = {
  MARKET_ACTIVITY: 'Market Activity',
  TRANSFORMING_ACTIVITY: 'Transforming Activity',
  PRODUCTION_MIX: 'Production Mix',
  IMPORT_ACTIVITY: 'Import Activity',
  MARKET_GROUP: 'Market Group',
  DOMESTIC_SUPPLY_MIX: 'Domestic Supply Mix',
  GLOBAL_SUPPLY_MIX: 'Global Supply Mix',
  SUPPLY_MIX: 'Supply Mix',
  IMPORT_MIX: 'Import Mix',
};

export const activityTypeCopy: Record<ActivityType, { text: React.ReactNode; link: string }> = {
  [ActivityType.MARKET_ACTIVITY]: {
    text: (
      <Stack spacing={3}>
        <Text>A market activity represents the average mix of suppliers for a product in a region.</Text>
        <Text>
          Market activities include transport from the production site to the point of consumption and any losses that occurred.
        </Text>
        <Text>
          For example, a concrete market shows all local concrete producers, how much each supplies, and the transportation
          needed to deliver it.
        </Text>
        <Text>Markets connect producers to consumers.</Text>
      </Stack>
    ),
    link: 'https://support.ecoinvent.org/market-activities',
  },
  [ActivityType.MARKET_GROUP]: {
    text: (
      <Stack spacing={3}>
        <Text>A market process represents the average mix of suppliers for a product in a region.</Text>
        <Text>
          Market processes include transport from the production site to the point of consumption and any losses that occurred.
        </Text>
        <Text>
          For example, a concrete market shows all local concrete producers, how much each supplies, and the transportation
          needed to deliver it.
        </Text>
        <Text>Markets connect producers to consumers.</Text>
      </Stack>
    ),
    link: 'https://support.ecoinvent.org/market-activities#:~:text=Australia%20to%20Europe.-,Market%20Groups%C2%A0,-A%20market%20group',
  },
  [ActivityType.PRODUCTION_MIX]: {
    text: (
      <Stack spacing={3}>
        <Text>
          Shows the blend of all suppliers producing a product in a region. Each supplier's contribution is weighted by how much
          they produce.
        </Text>
      </Stack>
    ),
    link: 'https://support.ecoinvent.org/markets',
  },
  [ActivityType.TRANSFORMING_ACTIVITY]: {
    text: (
      <Stack spacing={3}>
        <Text>
          A transforming activity converts inputs from the environment (e.g., natural resources) and the value chain (e.g., raw
          materials, energy, chemicals) into different outputs (products, wastes, emissions).
        </Text>
        <Text>
          For example, a hard coal mine that extracts hard coal from the ground to deliver the product hard coal, or the mango
          fruit being cultivated on a farm.
        </Text>
      </Stack>
    ),
    link: 'https://support.ecoinvent.org/activities-products#',
  },
  [ActivityType.IMPORT_ACTIVITY]: {
    text: (
      <Stack spacing={3}>
        <Text>
          Models the transfer of a product between regional markets. For example, 'hard coal, import from Australia' represents
          coal transported from the Australian market to the European market.
        </Text>
      </Stack>
    ),
    link: 'https://support.ecoinvent.org/market-activities#:~:text=Imports%20and%20Exports%C2%A0',
  },
  [ActivityType.DOMESTIC_SUPPLY_MIX]: {
    text: (
      <Stack spacing={3}>
        <Text>
          Combination of different producers using different technologies to produce the same product within the same geographic
          region. The DSM excludes production allocated to exports.
        </Text>
      </Stack>
    ),
    link: '',
  },
  [ActivityType.SUPPLY_MIX]: {
    text: (
      <Stack spacing={3}>
        <Text>
          It answers the question &ldquo;what is the proportion of the flow being produced by the domestic supply mix and import
          mix in this geographic region?&rdquo;
        </Text>
      </Stack>
    ),
    link: '',
  },
  [ActivityType.GLOBAL_SUPPLY_MIX]: {
    text: (
      <Stack spacing={3}>
        <Text>
          It answers the question &ldquo;what is the proportion of the flow being produced by the domestic supply mix and import
          mix in this geographic region?&rdquo;
        </Text>
      </Stack>
    ),
    link: '',
  },
  [ActivityType.IMPORT_MIX]: {
    text: (
      <Stack spacing={3}>
        <Text>
          The aggregated flow of a specific product into a receiving geographic region, representing the combined imports from
          all supplying geographic regions according to their respective shares. The Import mix excludes re-exports.
        </Text>
      </Stack>
    ),
    link: '',
  },
};

export const timePeriodCopy =
  'The time period does not represent an expiring date but the period of original data collection or the period to which the dataset has been extrapolated. If the end date is in the past it is still considered valid today, since processes or technologies did not change or the supply chain was updated.';
